import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Zap, Loader2 } from 'lucide-react';
import EnergyControlReportHero from '@/components/energy-control/EnergyControlReportHero';
import EnergyControlPreview from '@/components/energy-control/EnergyControlPreview';
import EnergyControlFullReport from '@/components/energy-control/EnergyControlFullReport';
import EnergyControlCTAs from '@/components/energy-control/EnergyControlCTAs';
import EnergyControlEmailReport from '@/components/energy-control/EnergyControlEmailReport';
import PowersureDeviceGridSection from '@/components/powersure-ui/PowersureDeviceGridSection';
import PowersureFAQSection from '@/components/powersure-ui/PowersureFAQSection';

const API = '/api';

export default function EnergyControlReportPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fullReportAnchor = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/energy-control/intake/${intakeId}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'Intake niet gevonden.' : 'Kon de gegevens niet laden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [intakeId]);

  const scrollToFullReport = () => {
    if (fullReportAnchor.current) {
      fullReportAnchor.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Open de accordion alvast zodat de klant direct het rapport ziet
      try {
        const det = fullReportAnchor.current.querySelector('details');
        if (det) det.open = true;
      } catch { /* ignore */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-xl mx-auto bg-white rounded-xl p-8 shadow-sm border border-border text-center" data-testid="ec-report-error">
            <h1 className="text-xl font-medium text-primary mb-2" style={{ fontFamily: 'Outfit' }}>{error || 'Er ging iets mis.'}</h1>
            <button
              onClick={() => navigate('/energy-control-intake')}
              className="btn-primary mt-4 inline-flex items-center gap-2 px-6 py-2 rounded-md"
              data-testid="ec-report-restart-btn"
            >
              Start de intake opnieuw
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 sm:py-10" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }} data-testid="ec-report-page">
      <div className="container mx-auto px-4 sm:px-6 mb-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
            <span className="text-base sm:text-xl font-light text-white" style={{ fontFamily: 'Outfit' }}>
              Energy Control Plan
            </span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-xs sm:text-sm text-white/60 hover:text-emerald-300"
            data-testid="ec-report-home-btn"
          >
            Naar home
          </button>
        </div>
      </div>

      {/* P0 mobile-clarity (Feb 2026) — bovenaan altijd: Wat Powersure doet,
          top 3 kansen, prijs €9,95, primaire CTA. Klant ziet binnen 10s waar
          het over gaat zonder eerst door 6 cards te scrollen. */}
      <EnergyControlReportHero
        intakeId={data.intake_id}
        preview={data.preview}
        fullReport={data.full_report}
        onScrollToReport={scrollToFullReport}
      />

      {/* Apparaten koppelen — visueel kernconcept tussen hero en preview.
          Geeft de klant snel een beeld van wat Powersure kan koppelen. */}
      <PowersureDeviceGridSection
        onTileClick={() => { /* eerst inschrijven, daarna koppelen */ }}
        onAddClick={() => { window.location.href = '/onboarding/devices'; }}
        title="Apparaten die u kunt koppelen"
        subtitle="Powersure ondersteunt deze routes. Eerst inzicht, daarna eventueel sturing."
      />

      {/* Preview — al compact (top 3, summary, CTA). Behouden onder de hero
          als korte gepersonaliseerde samenvatting. */}
      <EnergyControlPreview intakeId={data.intake_id} preview={data.preview} source="report" />

      {/* Volledig rapport en e-mail/CTA's gaan in accordions zodat de bovenkant
          rustig blijft. Klant kan elk blok op verzoek uitvouwen. */}
      <div ref={fullReportAnchor} className="container mx-auto px-4 sm:px-6 pb-6">
        <div className="max-w-2xl mx-auto space-y-3">
          <details
            className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 sm:px-5 py-3.5"
            data-testid="ec-report-accordion-full"
          >
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <span className="text-base font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                Toon volledig rapport
              </span>
              <span className="text-xs text-emerald-300 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-4 -mx-4 sm:-mx-5 rounded-xl bg-white text-foreground p-4">
              <EnergyControlFullReport report={data.full_report} />
            </div>
          </details>

          <details
            className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 sm:px-5 py-3.5"
            data-testid="ec-report-accordion-email"
          >
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <span className="text-base font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                Rapport per e-mail ontvangen
              </span>
              <span className="text-xs text-emerald-300 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 -mx-4 sm:-mx-5 rounded-xl bg-white text-foreground p-4">
              <EnergyControlEmailReport
                intakeId={data.intake_id}
                defaultEmail={data.email || ''}
              />
            </div>
          </details>

          <details
            className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-4 sm:px-5 py-3.5"
            data-testid="ec-report-accordion-ctas"
          >
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <span className="text-base font-medium text-white" style={{ fontFamily: 'Outfit' }}>
                Andere vervolgstappen
              </span>
              <span className="text-xs text-emerald-300 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 -mx-4 sm:-mx-5 rounded-xl bg-white text-foreground p-4">
              <EnergyControlCTAs intakeId={data.intake_id} />
            </div>
          </details>
        </div>
      </div>

      {/* FAQ — neemt onzekerheid weg vóór de klant verder gaat */}
      <PowersureFAQSection />
    </div>
  );
}
